import {useMemo, type MouseEvent} from "react";
import {Link} from "react-router-dom";
import type {DibDocument} from "./types";
import {buildTocTree, type TocEntry} from "./tocUtils";
import {buildTopicUrl} from "./contentUtils";
import classes from "./TableOfContents.module.css";

type Props = {
    document: DibDocument;
    activeId: string | null;
    onTocClick: (id: string) => void;
    /** Takes segment entry clicks instead of routing, for a host page that
     *  owns the URL. Without it, segment entries route to the topic page. */
    onSegmentClick?: (id: string) => void;
};

export const TableOfContents = ({
    document: doc,
    activeId,
    onTocClick,
    onSegmentClick,
}: Props) => {
    const entries = useMemo(() => buildTocTree(doc), [doc]);

    const expandedIds = useMemo(() => {
        if (!activeId) return new Set<string>();
        const ancestors = findAncestors(entries, activeId) ?? [];
        return new Set([...ancestors, activeId]);
    }, [entries, activeId]);

    if (entries.length === 0) return null;

    return (
        <ul className={classes.list}>
            {entries.map((entry) => (
                <TocItem
                    key={entry.id}
                    entry={entry}
                    topictypeId={doc.topictypeId}
                    name={doc.name}
                    topicId={doc.topicId}
                    activeId={activeId}
                    expandedIds={expandedIds}
                    onTocClick={onTocClick}
                    onSegmentClick={onSegmentClick}
                />
            ))}
        </ul>
    );
};

type TocItemProps = {
    entry: TocEntry;
    topictypeId: string;
    name: string;
    topicId: string;
    activeId: string | null;
    expandedIds: Set<string>;
    onTocClick: (id: string) => void;
    onSegmentClick?: (id: string) => void;
};

const TocItem = ({
    entry,
    topictypeId,
    name,
    topicId,
    activeId,
    expandedIds,
    onTocClick,
    onSegmentClick,
}: TocItemProps) => {
    const isActive = entry.id === activeId;
    const isExpanded = expandedIds.has(entry.id);
    const hasChildren = entry.children.length > 0;
    const isSegment = entry.seg === "1";

    const handleInPageClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onTocClick(entry.id);
    };

    // The prevented default stops the <Link>'s own navigation. The href
    // stays the topic URL, so a middle-click still opens the content page.
    const handleSegmentClick = onSegmentClick
        ? (e: MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              onSegmentClick(entry.id);
          }
        : undefined;

    const linkClass = isActive
        ? `${classes.link} ${classes.active}`
        : classes.link;

    const link = isSegment ? (
        <Link
            to={`${buildTopicUrl(topictypeId, name, topicId, entry.id)}#${entry.id}`}
            onClick={handleSegmentClick}
            className={linkClass}
            title={entry.title}
            aria-current={isActive ? "location" : undefined}
        >
            {entry.title}
        </Link>
    ) : (
        <a
            href={`#${entry.id}`}
            className={linkClass}
            onClick={handleInPageClick}
            title={entry.title}
            aria-current={isActive ? "location" : undefined}
        >
            {entry.title}
        </a>
    );

    return (
        <li>
            {link}
            {hasChildren && isExpanded && (
                <ul className={classes.children}>
                    {entry.children.map((child) => (
                        <TocItem
                            key={child.id}
                            entry={child}
                            topictypeId={topictypeId}
                            name={name}
                            topicId={topicId}
                            activeId={activeId}
                            expandedIds={expandedIds}
                            onTocClick={onTocClick}
                            onSegmentClick={onSegmentClick}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

// Returns ancestor IDs leading from root down to (but not including) targetId.
// Returns null if targetId is not in this subtree.
const findAncestors = (
    entries: TocEntry[],
    targetId: string
): string[] | null => {
    for (const entry of entries) {
        if (entry.id === targetId) return [];
        const subPath = findAncestors(entry.children, targetId);
        if (subPath !== null) return [entry.id, ...subPath];
    }
    return null;
};
